/* ============================================================
   Yiume Reconstructed — theme.js

   Vanilla JS, geen framework en geen build-stap. Alles is
   progressive enhancement: zonder JavaScript blijven links,
   formulieren en de winkelwagenpagina gewoon werken.

     1. Helpers
     2. Drawers (menu, zoeken, winkelwagen)
     3. Winkelwagen (add / update / remove)
     4. Announcement bar
     5. Slideshow
     6. Aantalkiezers
     7. Collectie: sorteren en filteren
     8. Aftelklok
     9. Aanbevelingen
    10. Verlanglijst (lokaal)
   ============================================================ */
(function () {
  'use strict';

  const T = window.theme || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── 1. Helpers ────────────────────────────────────────── */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function formatMoney(cents) {
    const fmt = T.moneyFormat || '${{amount}}';
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const [int, dec] = amount.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, grouped + '.' + dec)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grouped)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,
        int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec);
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.description || data.message || 'Request failed'), { data });
    return data;
  }

  /* ── 2. Drawers ────────────────────────────────────────── */
  const overlay = $('[data-overlay]');
  let openDrawer = null;

  function drawerOpen(id) {
    const el = document.getElementById(id);
    if (!el) return;
    drawerCloseAll();
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    overlay && overlay.classList.add('is-open');
    document.body.classList.add('is-locked');
    openDrawer = el;
    const focusable = el.querySelector('input, button, a[href]');
    focusable && focusable.focus({ preventScroll: true });
  }

  function drawerCloseAll() {
    $$('[data-drawer]').forEach((d) => {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    });
    overlay && overlay.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    openDrawer = null;
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      drawerOpen(opener.getAttribute('data-drawer-open'));
      return;
    }
    if (e.target.closest('[data-drawer-close]')) {
      e.preventDefault();
      drawerCloseAll();
      return;
    }
    if (overlay && e.target === overlay) drawerCloseAll();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openDrawer) drawerCloseAll();
    /* Focus binnen het geopende paneel houden. */
    if (e.key === 'Tab' && openDrawer) {
      const items = $$('a[href], button:not([disabled]), input, select, textarea', openDrawer)
        .filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* Winkelwagen opent als paneel wanneer dat is ingesteld. */
  if (T.cartType === 'drawer') {
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-cart-toggle]');
      if (!toggle) return;
      if (!document.getElementById('cart-drawer')) return;
      e.preventDefault();
      drawerOpen('cart-drawer');
    });
  }

  /* ── 3. Winkelwagen ────────────────────────────────────── */
  function updateCartUI(cart) {
    $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    $$('[data-cart-count-drawer]').forEach((el) => { el.textContent = '(' + cart.item_count + ')'; });
    $$('[data-cart-total]').forEach((el) => { el.textContent = formatMoney(cart.total_price); });

    const bar = $('[data-free-shipping]');
    if (bar) {
      const threshold = Number(bar.getAttribute('data-threshold')) || 0;
      const remaining = threshold - cart.total_price;
      const pct = threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 0;
      const fill = $('[data-free-shipping-fill]', bar);
      const text = $('[data-free-shipping-text]', bar);
      if (fill) fill.style.width = pct + '%';
      if (text) {
        text.textContent = remaining > 0
          ? (T.strings.freeShippingProgress || '').replace('[amount]', formatMoney(remaining))
          : (T.strings.freeShippingReached || '');
      }
    }

    /* Staffelkorting: zelfde teksten als snippets/bundle-discount-bar.liquid. */
    const bundle = $('[data-bundle-bar]');
    if (bundle) {
      const n = cart.item_count;
      const text = $('[data-bundle-text]', bundle);
      const fill = $('[data-bundle-fill]', bundle);
      if (text) {
        text.innerHTML = n <= 1 ? 'Add 1 more item and get <strong>10% off</strong> your order'
          : n === 2 ? '<strong>10% off</strong> unlocked! Add 1 more item for <strong>15% off</strong>'
          : n === 3 ? '<strong>15% off</strong> unlocked! Add 1 more item for <strong>20% off</strong>'
          : '<strong>20% off</strong> unlocked, our biggest discount!';
      }
      if (fill) fill.style.width = Math.min(100, (n / 4) * 100) + '%';
      $$('[data-bundle-tier]', bundle).forEach((li) => {
        li.classList.toggle('is-active', n >= Number(li.getAttribute('data-bundle-tier')));
      });
    }
  }

  /** Haalt de cart-drawer opnieuw op zodat de regels kloppen na een mutatie. */
  async function refreshCartDrawer() {
    const body = $('[data-cart-drawer-body]');
    if (!body) return;
    try {
      const res = await fetch(T.routes.cart || '/cart');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newBody = doc.querySelector('[data-cart-items]');
      const target = $('[data-cart-items]', body);
      if (newBody && target) target.replaceWith(newBody);
      else if (newBody) { body.innerHTML = ''; body.appendChild(newBody); }
      else window.location.reload();
    } catch { window.location.reload(); }
  }

  async function cartAdd(id, quantity = 1) {
    const cart = await postJSON((T.routes.cartAdd || '/cart/add') + '.js', { items: [{ id: Number(id), quantity }] });
    const state = await fetch((T.routes.cart || '/cart') + '.js').then((r) => r.json());
    updateCartUI(state);
    await refreshCartDrawer();
    if (T.cartType === 'drawer' && document.getElementById('cart-drawer')) drawerOpen('cart-drawer');
    return cart;
  }

  async function cartChange(key, quantity) {
    const state = await postJSON((T.routes.cartChange || '/cart/change') + '.js', { id: key, quantity });
    updateCartUI(state);
    if (quantity === 0 || state.item_count === 0) {
      if (document.body.classList.contains('template--cart')) { window.location.reload(); return state; }
    }
    await refreshCartDrawer();
    return state;
  }

  /* Toevoegen vanaf de productpagina */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const btn = $('[data-add-to-cart]', form);
    const label = $('[data-add-to-cart-text]', form);
    const original = label ? label.textContent : '';
    if (btn) btn.disabled = true;
    if (label) label.textContent = '…';
    try {
      const id = form.querySelector('[name="id"]').value;
      const qty = Number(form.querySelector('[name="quantity"]')?.value || 1);
      await cartAdd(id, qty);
      if (label) label.textContent = original;
    } catch (err) {
      if (label) label.textContent = (err.data && err.data.description) || T.strings.soldOut || 'Error';
      setTimeout(() => { if (label) label.textContent = original; }, 2500);
    } finally {
      if (btn) btn.disabled = false;
    }
  });

  /* Snel toevoegen vanaf een productkaart */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = '…';
    try {
      await cartAdd(btn.getAttribute('data-variant-id'), 1);
    } catch { /* stil falen: de gebruiker ziet de knop terugspringen */ }
    btn.textContent = original;
    btn.disabled = false;
  });

  /* Regel verwijderen */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-remove]');
    if (!btn) return;
    e.preventDefault();
    const row = btn.closest('[data-cart-item]');
    if (!row) return;
    row.style.opacity = '0.4';
    try { await cartChange(row.getAttribute('data-key'), 0); row.remove(); }
    catch { row.style.opacity = ''; }
  });

  /* Aantal in de winkelwagen wijzigen */
  let cartQtyTimer = null;
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-quantity]');
    if (!input) return;
    const row = input.closest('[data-cart-item]');
    if (!row) return;
    clearTimeout(cartQtyTimer);
    cartQtyTimer = setTimeout(async () => {
      const qty = Math.max(0, Number(input.value) || 0);
      try {
        await cartChange(row.getAttribute('data-key'), qty);
        if (qty === 0) row.remove();
      } catch { /* server houdt de waarheid; volgende render corrigeert */ }
    }, 350);
  });

  /* ── 4. Announcement bar ───────────────────────────────── */
  const announcement = $('[data-announcement]');
  if (announcement) {
    const items = $$('[data-announcement-item]', announcement);
    const speed = Number(announcement.getAttribute('data-rotate')) * 1000;
    if (items.length > 1 && speed > 0 && !reduceMotion) {
      let i = 0;
      setInterval(() => {
        items[i].classList.add('hidden');
        i = (i + 1) % items.length;
        items[i].classList.remove('hidden');
      }, speed);
    }
  }

  /* ── 5. Slideshow ──────────────────────────────────────── */
  $$('[data-slideshow]').forEach((root) => {
    const track = $('[data-slideshow-track]', root);
    const slides = $$('[data-slideshow-slide]', root);
    const dots = $$('[data-slideshow-dot]', root);
    if (!track || slides.length < 2) return;

    let index = 0;
    let timer = null;

    function go(next) {
      index = (next + slides.length) % slides.length;
      track.style.transition = reduceMotion ? 'none' : 'transform .5s cubic-bezier(.25,.46,.45,.94)';
      track.style.transform = 'translateX(-' + index * 100 + '%)';
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    }

    function start() {
      const autoplay = root.getAttribute('data-autoplay') === 'true';
      const interval = Number(root.getAttribute('data-interval')) || 6000;
      if (!autoplay || reduceMotion) return;
      stop();
      timer = setInterval(() => go(index + 1), interval);
    }
    function stop() { if (timer) clearInterval(timer); timer = null; }

    $('[data-slideshow-next]', root)?.addEventListener('click', () => { go(index + 1); start(); });
    $('[data-slideshow-prev]', root)?.addEventListener('click', () => { go(index - 1); start(); });
    dots.forEach((d) => d.addEventListener('click', () => { go(Number(d.getAttribute('data-index'))); start(); }));

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);

    /* Swipen op touch */
    let startX = 0;
    track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; stop(); }, { passive: true });
    track.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 45) go(index + (dx < 0 ? 1 : -1));
      start();
    }, { passive: true });

    go(0);
    start();
  });

  /* ── 6. Aantalkiezers ──────────────────────────────────── */
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-quantity-minus]');
    const plus = e.target.closest('[data-quantity-plus]');
    if (!minus && !plus) return;
    const wrap = (minus || plus).closest('.quantity');
    const input = $('input', wrap);
    if (!input) return;
    const min = Number(input.getAttribute('min') || 0);
    const current = Number(input.value) || 0;
    input.value = Math.max(min, current + (plus ? 1 : -1));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ── 7. Collectie: sorteren en filteren ────────────────── */
  const sortSelect = $('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  const facetForm = $('[data-facet-form]');
  if (facetForm) {
    facetForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const params = new URLSearchParams();
      $$('input:checked, input[type="number"]', facetForm).forEach((input) => {
        if (input.value) params.append(input.name, input.value);
      });
      const sort = sortSelect && sortSelect.value;
      if (sort) params.set('sort_by', sort);
      window.location.href = window.location.pathname + (params.toString() ? '?' + params : '');
    });
  }

  /* ── 8. Aftelklok ──────────────────────────────────────── */
  $$('[data-countdown]').forEach((root) => {
    const end = new Date(root.getAttribute('data-end')).getTime();
    if (!end || isNaN(end)) return;
    const ended = root.parentElement && root.parentElement.querySelector('[data-countdown-ended]');
    const set = (sel, v) => { const el = $(sel, root); if (el) el.textContent = String(v).padStart(2, '0'); };

    function tick() {
      const diff = end - Date.now();
      if (diff <= 0) {
        root.classList.add('hidden');
        ended && ended.classList.remove('hidden');
        clearInterval(iv);
        return;
      }
      const s = Math.floor(diff / 1000);
      set('[data-countdown-days]', Math.floor(s / 86400));
      set('[data-countdown-hours]', Math.floor((s % 86400) / 3600));
      set('[data-countdown-minutes]', Math.floor((s % 3600) / 60));
      set('[data-countdown-seconds]', s % 60);
    }
    const iv = setInterval(tick, 1000);
    tick();
  });

  /* ── 9. Aanbevelingen ──────────────────────────────────── */
  const rec = $('[data-recommendations]');
  if (rec && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(async (entry) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        try {
          const res = await fetch(rec.getAttribute('data-url'));
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('[data-recommendations]');
          if (fresh) rec.innerHTML = fresh.innerHTML;
        } catch { /* aanbevelingen zijn optioneel */ }
      });
    }, { rootMargin: '400px' });
    io.observe(rec);
  }

  /* ── 10. Verlanglijst ──────────────────────────────────── */
  const WISHLIST_KEY = 'yiume:wishlist';
  const readWishlist = () => {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch { return []; }
  };
  const paintWishlist = () => {
    const list = readWishlist();
    $$('[data-wishlist-toggle]').forEach((btn) => {
      const on = list.includes(btn.getAttribute('data-product-handle'));
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', String(on));
    });
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.getAttribute('data-product-handle');
    const list = readWishlist();
    const next = list.includes(handle) ? list.filter((h) => h !== handle) : list.concat(handle);
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    paintWishlist();
  });
  paintWishlist();
})();
